import React, { useState, useEffect } from 'react';
import { FileText, TrendingUp, AlertCircle, Calendar, Target, Loader } from 'lucide-react';
import { sessionNoteService } from '../services/SessionNoteService';
import { SessionNote } from '../models/SessionNote';
import { useAuth } from '../contexts/AuthContext';

interface SessionPreparationProps {
  clientId: string;
  clientName: string;
  onContinue: () => void;
}

export const SessionPreparation: React.FC<SessionPreparationProps> = ({
  clientId,
  clientName,
  onContinue,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [previousNotes, setPreviousNotes] = useState<SessionNote[]>([]);
  const [expandedNoteId, setExpandedNoteId] = useState<string | null>(null);

  useEffect(() => {
    loadPreviousNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const loadPreviousNotes = async () => {
    if (!user) {
      setPreviousNotes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Get last 3 approved session notes this RBT authored for this client
      // ("own notes only" policy).
      const notes = await sessionNoteService.getSessionNotesByClient(clientId, user.id);
      const approvedNotes = notes
        .filter((note) => note.status === 'approved')
        .sort((a, b) => {
          const aTime = a.timestamps?.sessionStart?.toMillis() || 0;
          const bTime = b.timestamps?.sessionStart?.toMillis() || 0;
          return bTime - aTime;
        })
        .slice(0, 3);

      setPreviousNotes(approvedNotes);
    } catch (err) {
      console.error('Error loading previous notes:', err);
      // Don't block the flow - just show no notes
      setPreviousNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleNote = (noteId: string) => {
    setExpandedNoteId(expandedNoteId === noteId ? null : noteId);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    return timestamp.toDate().toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  // Inline styles
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '24px',
    },
    headerBox: {
      backgroundColor: '#eef2ff',
      border: '1px solid #c7d2fe',
      borderRadius: '8px',
      padding: '16px',
    },
    headerContent: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
    },
    headerIcon: {
      width: '24px',
      height: '24px',
      color: '#4f46e5',
      marginTop: '2px',
      flexShrink: 0,
    },
    headerTitle: {
      fontSize: '18px',
      fontWeight: 600,
      color: '#312e81',
      margin: 0,
    },
    headerDescription: {
      fontSize: '14px',
      color: '#4338ca',
      marginTop: '4px',
      margin: 0,
    },
    headerHint: {
      fontSize: '12px',
      color: '#4f46e5',
      marginTop: '8px',
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 0',
    },
    loadingSpinner: {
      width: '32px',
      height: '32px',
      color: '#4f46e5',
      animation: 'spin 1s linear infinite',
    },
    loadingText: {
      marginLeft: '12px',
      color: '#374151',
    },
    sectionTitle: {
      fontSize: '14px',
      fontWeight: 600,
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      margin: 0,
      marginBottom: '16px',
    },
    noteCard: {
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      overflow: 'hidden',
      marginBottom: '16px',
    },
    noteButton: {
      width: '100%',
      padding: '16px',
      textAlign: 'left' as const,
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    noteHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    noteDateBadgeContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    noteDate: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#2d2d2d',
    },
    approvedBadge: {
      fontSize: '12px',
      padding: '2px 8px',
      backgroundColor: '#dcfce7',
      color: '#15803d',
      borderRadius: '9999px',
      fontWeight: 500,
    },
    noteSubtext: {
      fontSize: '12px',
      color: '#6b6b6b',
      marginTop: '4px',
    },
    expandIcon: {
      color: '#9ca3af',
    },
    expandedContent: {
      borderTop: '1px solid #e5e0db',
      padding: '16px',
      backgroundColor: '#f9fafb',
    },
    sectionHeader: {
      fontSize: '12px',
      fontWeight: 600,
      color: '#374151',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    sectionIcon: {
      width: '12px',
      height: '12px',
    },
    summaryText: {
      fontSize: '14px',
      color: '#1f2937',
      margin: 0,
    },
    programCard: {
      backgroundColor: '#ffffff',
      border: '1px solid #e5e0db',
      borderRadius: '4px',
      padding: '12px',
      marginBottom: '8px',
    },
    programHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '4px',
    },
    programName: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#2d2d2d',
    },
    programScore: {
      fontSize: '12px',
      color: '#6b6b6b',
    },
    programDetails: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '12px',
      color: '#6b6b6b',
    },
    programNotes: {
      fontSize: '12px',
      color: '#374151',
      marginTop: '8px',
    },
    concernItem: {
      fontSize: '14px',
      color: '#1f2937',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      marginBottom: '4px',
    },
    concernBullet: {
      color: '#ef4444',
      marginTop: '2px',
    },
    supervisorBox: {
      backgroundColor: '#eff6ff',
      border: '1px solid #bfdbfe',
      borderRadius: '4px',
      padding: '12px',
      marginTop: '16px',
    },
    supervisorTitle: {
      fontSize: '12px',
      fontWeight: 600,
      color: '#1e3a8a',
      marginBottom: '8px',
    },
    supervisorText: {
      fontSize: '14px',
      color: '#1e40af',
      margin: 0,
    },
    noNotesBox: {
      backgroundColor: '#fefce8',
      border: '1px solid #fde047',
      borderRadius: '8px',
      padding: '16px',
    },
    noNotesContent: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
    },
    noNotesIcon: {
      width: '20px',
      height: '20px',
      color: '#ca8a04',
      marginTop: '2px',
      flexShrink: 0,
    },
    noNotesTitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#854d0e',
      margin: 0,
    },
    noNotesText: {
      fontSize: '12px',
      color: '#a16207',
      marginTop: '4px',
    },
    continueButton: {
      width: '100%',
      padding: '12px 24px',
      backgroundColor: '#4F6367',
      color: '#ffffff',
      borderRadius: '8px',
      border: 'none',
      fontWeight: 500,
      fontSize: '16px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'background-color 0.2s',
    },
    infoList: {
      fontSize: '12px',
      color: '#6b6b6b',
      margin: 0,
      padding: 0,
      listStyle: 'none',
    },
    infoItem: {
      marginBottom: '4px',
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.headerBox}>
        <div style={styles.headerContent}>
          <FileText style={styles.headerIcon} />
          <div>
            <h3 style={styles.headerTitle}>Session Preparation</h3>
            <p style={styles.headerDescription}>
              Review recent session notes and client progress for {clientName}
            </p>
            <p style={styles.headerHint}>
              Understanding previous sessions helps you provide consistent, effective care
            </p>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={styles.loadingContainer}>
          <Loader style={styles.loadingSpinner} />
          <span style={styles.loadingText}>Loading previous session notes...</span>
        </div>
      )}

      {/* Previous Notes */}
      {!loading && previousNotes.length > 0 && (
        <div>
          <h4 style={styles.sectionTitle}>
            <Calendar style={{ width: '16px', height: '16px' }} />
            Recent Sessions ({previousNotes.length})
          </h4>

          {previousNotes.map((note) => (
            <div key={note.id} style={styles.noteCard}>
              <button
                onClick={() => toggleNote(note.id)}
                style={styles.noteButton}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <div style={styles.noteHeader}>
                  <div style={{ flex: 1 }}>
                    <div style={styles.noteDateBadgeContainer}>
                      <span style={styles.noteDate}>
                        {formatDate(note.timestamps?.sessionStart)}
                      </span>
                      <span style={styles.approvedBadge}>Approved</span>
                    </div>
                    <p style={styles.noteSubtext}>
                      RBT: {note.rbtName} • Supervisor: {note.supervisorName}
                    </p>
                  </div>
                  <div style={styles.expandIcon}>
                    {expandedNoteId === note.id ? '▲' : '▼'}
                  </div>
                </div>
              </button>

              {/* Expanded Content */}
              {expandedNoteId === note.id && (
                <div style={styles.expandedContent}>
                  {/* Session Summary */}
                  {note.rbtDraft?.sessionSummary && (
                    <div style={{ marginBottom: '16px' }}>
                      <h5 style={styles.sectionHeader}>
                        <FileText style={styles.sectionIcon} />
                        Session Summary
                      </h5>
                      <p style={styles.summaryText}>{note.rbtDraft.sessionSummary}</p>
                    </div>
                  )}

                  {/* Program Data */}
                  {note.rbtDraft?.programData && note.rbtDraft.programData.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <h5 style={styles.sectionHeader}>
                        <Target style={styles.sectionIcon} />
                        Programs Worked On
                      </h5>
                      {note.rbtDraft.programData.map((program, idx) => (
                        <div key={idx} style={styles.programCard}>
                          <div style={styles.programHeader}>
                            <span style={styles.programName}>{program.programName}</span>
                            <span style={styles.programScore}>
                              {program.correctResponses}/{program.trialsCompleted} correct
                            </span>
                          </div>
                          <div style={styles.programDetails}>
                            <span>Prompt: {program.promptLevel}</span>
                          </div>
                          {program.notes && (
                            <p style={styles.programNotes}>{program.notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Behavior Data */}
                  {note.rbtDraft?.behaviorData && note.rbtDraft.behaviorData.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <h5 style={styles.sectionHeader}>
                        <TrendingUp style={styles.sectionIcon} />
                        Behaviors Observed
                      </h5>
                      {note.rbtDraft.behaviorData.map((behavior, idx) => (
                        <div key={idx} style={styles.programCard}>
                          <div style={styles.programHeader}>
                            <span style={styles.programName}>{behavior.behaviorName}</span>
                            <span style={styles.programScore}>
                              Frequency: {behavior.frequency}
                            </span>
                          </div>
                          {behavior.antecedents && (
                            <p style={styles.programNotes}>
                              <span style={{ fontWeight: 500 }}>Antecedents:</span>{' '}
                              {behavior.antecedents}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Concerns */}
                  {note.rbtDraft?.concerns && note.rbtDraft.concerns.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <h5 style={styles.sectionHeader}>
                        <AlertCircle style={styles.sectionIcon} />
                        Concerns Noted
                      </h5>
                      {note.rbtDraft.concerns
                        .filter((c) => c.trim())
                        .map((concern, idx) => (
                          <div key={idx} style={styles.concernItem}>
                            <span style={styles.concernBullet}>•</span>
                            {concern}
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Supervisor Feedback */}
                  {note.supervisorFinal?.supervisionFeedback && (
                    <div style={styles.supervisorBox}>
                      <h5 style={styles.supervisorTitle}>Supervisor Feedback</h5>
                      <p style={styles.supervisorText}>
                        {note.supervisorFinal.supervisionFeedback}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* No Previous Notes */}
      {!loading && previousNotes.length === 0 && (
        <div style={styles.noNotesBox}>
          <div style={styles.noNotesContent}>
            <AlertCircle style={styles.noNotesIcon} />
            <div>
              <p style={styles.noNotesTitle}>No Previous Sessions</p>
              <p style={styles.noNotesText}>
                This is the first session with {clientName}, or no approved notes are available yet.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Continue Button */}
      <button
        onClick={onContinue}
        style={styles.continueButton}
        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#3d4d50')}
        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#4F6367')}
      >
        Continue to Checklist
      </button>

      {/* Info */}
      <ul style={styles.infoList}>
        <li style={styles.infoItem}>• Review this information to ensure session continuity</li>
        <li style={styles.infoItem}>• Note any patterns in behavior or program progress</li>
        <li style={styles.infoItem}>• Refer back to supervisor feedback for guidance</li>
      </ul>
    </div>
  );
};
